import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Various text range utilities.
 * Copied from: https://github.com/AleksandrHovhannisyan/text-range-utils/blob/master/src/index.ts
 * MIT License - Copyright (c) 2024 Aleksandr Hovhannisyan
 */
// https://x.com/mattpocockuk/status/1821926395380986219
type LooseStringUnion<T> = T | (string & {});

export type GetTextNodesOptions = {
  /** Any text node that is a descendant of one of these tags will be ignored. */
  disallowedAncestorTags?: LooseStringUnion<keyof HTMLElementTagNameMap>[];
};

export type WrapTextNodeOptions = {
  /** The character offset to start from (inclusive). If not specified, wraps from beginning of node. */
  startOffset?: number;
  /** The character to end at (exclusive). If not specified, wraps until end of node. */
  endOffset?: number;
};

export type WrapRangeTextNodesOptions = GetTextNodesOptions & {
  /** Optional callback to filter which text nodes are wrapped. If this function returns `true`, `node` will be wrapped; else, it will be ignored. */
  shouldWrapNode?: (node: Text) => boolean;
};

/** A callback that unwraps a node. */
export type UnwrapFn = () => void;

/** Returns all of the text nodes that intersect with the given range.
 * @param range The Range to search for text nodes.
 * @param options Customization options for node searching behavior.
 */
export function getTextNodesInRange(range: Range, options?: GetTextNodesOptions): Node[] {
  const { disallowedAncestorTags } = {
    ...options,
  };
  const nodes: Node[] = [];

  const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_TEXT, (node: Node) => {
    const immediateParent = node.parentElement;
    if (immediateParent && disallowedAncestorTags?.some((tag) => !!immediateParent.closest(tag.toLowerCase()))) {
      return NodeFilter.FILTER_REJECT;
    }
    return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
  });
  // commonAncestorContainer would be a text node only if the range encompasses pure text. Otherwise, if it spans element boundaries, get nextNode.
  let currentNode = walker.currentNode.nodeType === Node.TEXT_NODE ? walker.currentNode : walker.nextNode();
  while (currentNode) {
    nodes.push(currentNode);
    currentNode = walker.nextNode();
  }
  return nodes;
}

/** Returns all text nodes that are partially or fully selected.
 * @param selection A selection spanning the text nodes to return.
 * @param options Customization options.
 */
export function getSelectedTextNodes(selection: Selection, options?: GetTextNodesOptions): Node[] {
  const allTextNodes: Node[] = [];
  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);
    const textNodesInRange = getTextNodesInRange(range, options);
    allTextNodes.push(...textNodesInRange);
  }
  return allTextNodes;
}

/** Wraps the given node's contents in the range `[startOffset, endOffset)`. Returns a function to unwrap the text node.
 * @param node The text node to wrap.
 * @param wrapperElement Element with which to wrap the text node. The node will be cloned before wrapping.
 * @param options Customization options
 */
export function wrapTextNode(node: Text, wrapperElement: Node, options?: WrapTextNodeOptions): UnwrapFn {
  // Ignore non-text nodes.
  if (node.nodeType !== Node.TEXT_NODE) {
    return () => {};
  }

  // Select appropriate portion of the text node
  const range = document.createRange();
  range.selectNodeContents(node);
  if (typeof options?.startOffset !== 'undefined') {
    range.setStart(node, options.startOffset);
  }
  if (typeof options?.endOffset !== 'undefined') {
    range.setEnd(node, options?.endOffset);
  }

  // Finally, wrap it
  const newParent = wrapperElement.cloneNode(true);
  range.surroundContents(newParent);

  return function unwrapTextNode() {
    const parent = newParent.parentNode;
    if (parent) {
      // First child is the node we wrapped
      parent.insertBefore(newParent.firstChild!, newParent);
      parent.removeChild(newParent);
    }
  };
}

/** Wraps each text node in a range with the given wrapper element. Returns a function to unwrap each wrapped text node.
 * @param range A Range spanning the text nodes to wrap.
 * @param wrapper An element with which to wrap each text node.
 * @param options Customization options.
 */
export function wrapRangeTextNodes(range: Range, wrapper: HTMLElement, options?: WrapRangeTextNodesOptions): UnwrapFn {
  const { shouldWrapNode, ...getTextNodeOptions } = { ...options };
  const unwrapCallbacks: UnwrapFn[] = [];
  const selectedTextNodes = getTextNodesInRange(range, getTextNodeOptions);
  selectedTextNodes.forEach((node) => {
    if (typeof shouldWrapNode !== 'undefined' && !shouldWrapNode(node as Text)) {
      return;
    }
    const startOffset = node === range.startContainer ? range.startOffset : undefined;
    const endOffset = node === range.endContainer ? range.endOffset : undefined;
    const unwrap = wrapTextNode(node as Text, wrapper, { startOffset, endOffset });
    unwrapCallbacks.push(unwrap);
  });
  return function unwrapRangeTextNodes() {
    unwrapCallbacks.forEach((unwrap) => unwrap());
  };
}

/** Wraps each eligible text node in the selection with the given wrapper element. Returns a function to unwrap each wrapped text node.
 * @param selection A selection spanning the text nodes to wrap.
 * @param wrapper The HTML element with which to wrap each eligible text node.
 * @param options Customization options.
 */
export function wrapSelectedTextNodes(
  selection: Selection,
  wrapper: HTMLElement,
  options?: WrapRangeTextNodesOptions
): UnwrapFn {
  const unwrapCallbacks: UnwrapFn[] = [];
  // Firefox supports multiple ranges
  for (let rangeIndex = 0; rangeIndex < selection.rangeCount; rangeIndex++) {
    const range = selection.getRangeAt(rangeIndex);
    const unwrap = wrapRangeTextNodes(range, wrapper, options);
    unwrapCallbacks.push(unwrap);
  }
  return function unwrapSelectedTextNodes() {
    unwrapCallbacks.forEach((unwrap) => unwrap());
  };
}

/**
 * Calculate the luminance of a hex color
 * @param hex - Hex color string (with or without #, supports 3 or 6 character format)
 * @returns Luminance value between 0 and 1
 */
function getLuminance(hex: string): number {
  // Remove # if present and convert to lowercase
  let cleanHex = hex.replace('#', '').toLowerCase();

  // Validate hex string contains only valid hex characters
  if (!/^[0-9a-f]+$/.test(cleanHex)) {
    console.warn(`Invalid hex color: ${hex}. Using fallback color.`);
    cleanHex = '000000'; // Default to black for invalid input
  }

  // Handle 3-character hex codes by expanding them to 6 characters
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // Validate length after expansion
  if (cleanHex.length !== 6) {
    console.warn(`Invalid hex color length: ${hex}. Using fallback color.`);
    cleanHex = '000000'; // Default to black for invalid length
  }

  // Convert to RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const sR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const sG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const sB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  // Calculate luminance
  return 0.2126 * sR + 0.7152 * sG + 0.0722 * sB;
}

/**
 * Get appropriate text color (black or white) based on background color
 * @param backgroundColor - Hex color string (with or without #)
 * @returns 'black' or 'white'
 */
export function getContrastTextColor(backgroundColor: string): 'black' | 'white' {
  const luminance = getLuminance(backgroundColor);
  // Use white text on dark backgrounds, black text on light backgrounds
  return luminance > 0.5 ? 'black' : 'white';
}
